import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { getActiveServices, Service } from '../utils/supabaseExtended';
import ServiceRequestForm from '../components/ui/ServiceRequestForm';
import Button from '../components/ui/Button';
import Spinner from '../components/ui/Spinner';
import { Helmet } from 'react-helmet';
import Card from '../components/ui/Card';

// Styled components
const PageContainer = styled.div`
  background-color: #000;
  color: #fff;
  min-height: 100vh;
  padding-top: 70px;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 3rem 1rem;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1.5rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 2px;
    background-color: #004744;
  }
`;

const PageDescription = styled.p`
  max-width: 800px;
  margin: 0 auto;
  color: #888;
  font-size: 1.2rem;
  line-height: 1.6;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FilterButton = styled.button<{ $active?: boolean }>`
  padding: 0.75rem 1.5rem;
  border: 2px solid #004744;
  background: ${props => props.$active ? '#004744' : 'transparent'};
  color: ${props => props.$active ? '#fff' : '#004744'};
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #004744;
    color: #fff;
    transform: translateY(-2px);
  }
`;

const SearchContainer = styled.div`
  max-width: 500px;
  margin: 0 auto 3rem;
  position: relative;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #333;
  background: #111;
  color: #fff;
  border-radius: 12px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #004744;
  }
  
  &::placeholder {
    color: #666;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled(motion.div)`
  background: linear-gradient(135deg, #111 0%, #1a1a1a 100%);
  border: 1px solid #333;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #004744;
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 71, 68, 0.2);
  }
`;

const ServiceImage = styled.div<{ $image?: string }>`
  height: 200px;
  background: ${props => props.$image ? 
    `linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)), url(${props.$image})` : 
    'linear-gradient(135deg, #004744 0%, #004744 100%)'
  };
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  background: rgba(0, 71, 68, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
`;

const ServiceContent = styled.div`
  padding: 1.5rem;
`;

const ServiceCategory = styled.span`
  display: inline-block;
  background: #004744;
  color: #fff;
  padding: 0.25rem 0.75rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const ServiceTitle = styled.h3`
  font-size: 1.3rem;
  margin-bottom: 0.75rem;
  color: #fff;
`;

const ServiceDescription = styled.p`
  color: #ccc;
  line-height: 1.5;
  margin-bottom: 1rem;
  font-size: 0.95rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const ServiceMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #333;
`;

const ServiceProvider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #004744;
  font-size: 0.9rem;
  font-weight: 500;
`;

const ServicePrice = styled.div`
  color: #004744;
  font-weight: 600;
  font-size: 1.1rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

// Icone per categorie
const getCategoryIcon = (category: string): string => {
  switch (category.toLowerCase()) {
    case 'wellness':
      return '🧘';
    case 'catering':
      return '🍽️';
    case 'courses':
      return '📚';
    case 'events':
      return '🎉';
    case 'fitness':
      return '💪';
    case 'culture':
      return '🎭';
    default:
      return '⭐';
  }
};

const ServicesCatalog: React.FC = () => {
  const { language } = useLanguage();
  const [services, setServices] = useState<Service[]>([]);
  const [filteredServices, setFilteredServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showRequestForm, setShowRequestForm] = useState(false);

  // Carica servizi
  useEffect(() => {
    loadServices();
  }, []);

  // Filtra servizi quando cambiano categoria, ricerca o servizi (spostato sotto la definizione)

  const loadServices = async () => {
    setLoading(true);
    try {
      const services = await getActiveServices();
      setServices(services);
      setError(null);
    } catch (err) {
      setError('Errore durante il caricamento dei servizi');
    } finally {
      setLoading(false);
    }
  };

  const filterServices = React.useCallback(() => {
    let filtered = services;

    // Filtra per categoria
    if (activeCategory !== 'all') {
      filtered = filtered.filter(service => service.category === activeCategory);
    }

    // Filtra per ricerca
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(service =>
        (language === 'it' ? service.title_it : service.title_en).toLowerCase().includes(searchLower) ||
        (language === 'it' ? service.description_it : service.description_en)?.toLowerCase().includes(searchLower) ||
        (service as any).provider?.name?.toLowerCase().includes(searchLower)
      );
    }

    setFilteredServices(filtered);
  }, [services, activeCategory, searchTerm, language]);

  // Filtra servizi quando cambiano categoria, ricerca o servizi
  useEffect(() => {
    filterServices();
  }, [filterServices]);

  const handleCategoryFilter = (category: string) => {
    setActiveCategory(category);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ottieni categorie uniche
  const categories = ['all', ...Array.from(new Set(services.map(service => service.category)))];

  const formatPrice = (service: Service): string => {
    if (service.price_per_person) {
      return `€${service.price_per_person}/persona`;
    }
    if (service.price_per_group) {
      return `€${service.price_per_group}/gruppo`;
    }
    return 'Su richiesta';
  };

  const getCategoryLabel = (category: string): string => {
    const labels: { [key: string]: { it: string; en: string } } = {
      all: { it: 'Tutti', en: 'All' },
      wellness: { it: 'Benessere', en: 'Wellness' },
      catering: { it: 'Catering', en: 'Catering' },
      courses: { it: 'Corsi', en: 'Courses' },
      events: { it: 'Eventi', en: 'Events' },
      fitness: { it: 'Fitness', en: 'Fitness' },
      culture: { it: 'Cultura', en: 'Culture' }
    };
    
    return labels[category] ? 
      (language === 'it' ? labels[category].it : labels[category].en) : 
      category;
  };

  const handleServiceSelect = (service: Service) => {
    setSelectedService(service);
    setCurrentImageIndex(0);
    setShowRequestForm(false);
  };

  const handleCloseRequestForm = () => {
    setSelectedService(null);
    setShowRequestForm(false);
  };

  const handleRequestSuccess = () => {
    handleCloseRequestForm();
    // Opzionalmente, mostra un messaggio di successo o reindirizza alla dashboard
  };

  return (
    <PageContainer>
      <Helmet>
        <title>{language === 'it' ? 'Catalogo Servizi | J-O-H' : 'Services Catalog | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Scopri tutti i nostri servizi: yoga, meditazione, catering, corsi e molto altro. Prenota la tua esperienza personalizzata.' 
            : 'Discover all our services: yoga, meditation, catering, courses and much more. Book your personalized experience.'} 
        />
      </Helmet>

      <ContentContainer>
        <Header>
          <PageTitle>
            {language === 'it' ? 'Catalogo Servizi' : 'Services Catalog'}
          </PageTitle>
          <PageDescription>
            {language === 'it'
              ? 'Scopri la nostra ampia gamma di servizi personalizzati. Dalla wellness al catering, dai corsi agli eventi, abbiamo tutto quello che ti serve per rendere speciale la tua esperienza.'
              : 'Discover our wide range of personalized services. From wellness to catering, from courses to events, we have everything you need to make your experience special.'}
          </PageDescription>
        </Header>

        {/* Ricerca */}
        <SearchContainer>
          <SearchInput
            type="text"
            placeholder={language === 'it' ? '🔍 Cerca servizi...' : '🔍 Search services...'}
            value={searchTerm}
            onChange={handleSearch}
          />
        </SearchContainer>

        {/* Filtri categorie */}
        <FiltersContainer>
          {categories.map(category => (
            <FilterButton
              key={category}
              $active={activeCategory === category}
              onClick={() => handleCategoryFilter(category)}
            >
              {getCategoryIcon(category)} {getCategoryLabel(category)}
            </FilterButton>
          ))}
        </FiltersContainer>

        {/* Loading */}
        {loading && (
          <LoadingContainer>
            <Spinner />
          </LoadingContainer>
        )}

        {/* Error */}
        {error && (
          <NoResults>
            <h3>Errore</h3>
            <p>{error}</p>
            <Button variant="primary" onClick={loadServices}>
              {language === 'it' ? 'Riprova' : 'Try again'}
            </Button>
          </NoResults>
        )}

        {/* Servizi */}
        {!loading && !error && (
          <>
            {filteredServices.length === 0 ? (
              <NoResults>
                <h3>
                  {language === 'it' ? 'Nessun servizio trovato' : 'No services found'}
                </h3>
                <p>
                  {language === 'it' 
                    ? 'Prova a cambiare i filtri o la ricerca'
                    : 'Try changing filters or search terms'}
                </p>
              </NoResults>
            ) : (
              <ServicesGrid>
                {filteredServices.map((service, index) => (
                  <div key={service.id}>
                    <Card
                      image={service.images?.[0] || ''}
                      title={language === 'it' ? service.title_it : service.title_en}
                      subtitle={`${getCategoryLabel(service.category)} • ${(service as any).provider?.name || ''}`}
                      description={(language === 'it' ? service.description_it : service.description_en) || ''}
                      price={formatPrice(service)}
                      hoverEffect={true}
                      onClick={() => handleServiceSelect(service)}
                      delay={index * 0.05}
                    />
                  </div>
                ))}
              </ServicesGrid>
            )}
          </>
        )}
      </ContentContainer>
      {/* Dettaglio servizio + richiesta */}
      {selectedService && (
        <ModalOverlay onClick={handleCloseRequestForm}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={handleCloseRequestForm}>×</CloseButton>
            <DetailImageContainer>
              <DetailImage
                src={(selectedService.images && selectedService.images.length > 0)
                  ? selectedService.images[currentImageIndex]
                  : ''}
                alt={language === 'it' ? selectedService.title_it : selectedService.title_en}
              />
              {selectedService.images && selectedService.images.length > 1 && (
                <DetailImageNav>
                  <NavButton onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((idx) => (idx - 1 + selectedService.images!.length) % selectedService.images!.length); }}>←</NavButton>
                  <NavButton onClick={(e) => { e.stopPropagation(); setCurrentImageIndex((idx) => (idx + 1) % selectedService.images!.length); }}>→</NavButton>
                </DetailImageNav>
              )}
            </DetailImageContainer>

            <DetailBody>
              <DetailTitle>{language === 'it' ? selectedService.title_it : selectedService.title_en}</DetailTitle>
              <DetailMetaRow>
                <DetailBadge>{getCategoryLabel(selectedService.category)}</DetailBadge>
                <DetailPrice>{formatPrice(selectedService)}</DetailPrice>
              </DetailMetaRow>
              {(selectedService as any).provider?.name && (
                <div style={{ color: '#4a5568', marginBottom: '1rem' }}>
                  👤 {(selectedService as any).provider?.name}
                </div>
              )}
              <DetailDescription>
                {language === 'it' ? selectedService.description_it : selectedService.description_en}
              </DetailDescription>
              {!showRequestForm && (
                <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
                  <Button variant="primary" onClick={() => setShowRequestForm(true)}>
                    {language === 'it' ? 'Richiedi questo servizio' : 'Request this service'}
                  </Button>
                </div>
              )}
              {showRequestForm && (
                <div style={{ marginTop: '1.5rem' }}>
                  <ServiceRequestForm service={selectedService} onSuccess={handleRequestSuccess} />
                </div>
              )}
            </DetailBody>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

// Styled components per il modal dettagli (ispirati alla pagina Alloggi)
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.6));
  backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafb 100%);
  border-radius: 24px;
  max-width: 900px;
  width: 100%;
  max-height: 95vh;
  overflow-y: auto;
  position: relative;
  color: #2d3748;
  box-shadow: 
    0 25px 50px -12px rgba(0, 0, 0, 0.25),
    0 0 0 1px rgba(255, 255, 255, 0.8);
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  background: linear-gradient(135deg, #ffffff 0%, #f7fafc 100%);
  border: none;
  border-radius: 50%;
  width: 3rem;
  height: 3rem;
  font-size: 1.25rem;
  color: #4a5568;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  z-index: 110;
  box-shadow: 
    0 10px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.9),
    inset 0 1px 0 rgba(255, 255, 255, 0.8);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  &:hover {
    transform: scale(1.1) rotate(90deg);
    background: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
    color: white;
  }
`;

const DetailImageContainer = styled.div`
  position: relative;
  height: 420px;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  overflow: hidden;
`;

const DetailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const DetailImageNav = styled.div`
  position: absolute;
  top: 50%;
  width: 100%;
  display: flex;
  justify-content: space-between;
  transform: translateY(-50%);
  padding: 0 1.5rem;
  z-index: 2;
`;

const NavButton = styled.button`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(247, 250, 252, 0.9) 100%);
  border: none;
  border-radius: 50%;
  width: 3.5rem;
  height: 3.5rem;
  font-size: 1.25rem;
  color: #2d3748;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  backdrop-filter: blur(10px);
  box-shadow: 
    0 8px 25px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.8);
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
`;

const DetailBody = styled.div`
  padding: 2rem;
`;

const DetailTitle = styled.h2`
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #1a202c;
`;

const DetailMetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
`;

const DetailBadge = styled.span`
  display: inline-block;
  background: #004744;
  color: #fff;
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
`;

const DetailPrice = styled.div`
  color: #004744;
  font-weight: 700;
  font-size: 1.25rem;
`;

const DetailDescription = styled.div`
  font-size: 1rem;
  line-height: 1.7;
  color: #4a5568;
  padding: 1rem 0;
`;

export default ServicesCatalog; 
